<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import OddsCard from './Event/OddsCard.vue'
import { formatSportTitle } from '@/services/utils.js'
import { useUserStore } from '@/stores/user'

// Composables
const { t } = useI18n()
const userStore = useUserStore()

// Props
const props = defineProps({
  event: {
    type: Object,
    required: true
  }
})

// Reactive data
const showAllOdds = ref(false)

// Methods

const getEventStatus = (commenceTime) => {
  // Check if event has score data and is completed
  if (props.event.score && props.event.score.completed) {
    return { text: t('events.status.finished'), color: 'success', key: 'finished' }
  }
  
  const now = new Date()
  const eventTime = new Date(commenceTime)
  const diffHours = (eventTime - now) / (1000 * 60 * 60)

  if (diffHours < 0) {
    return { text: t('events.status.inProgress'), color: 'warning', key: 'inProgress' }
  } else if (diffHours < 2) {
    return { text: t('events.status.startingSoon'), color: 'error', key: 'startingSoon' }
  } else if (diffHours < 24) {
    return { text: t('events.status.today'), color: 'info', key: 'today' }
  } else {
    return { text: t('events.status.scheduled'), color: 'primary', key: 'scheduled' }
  }
}

const hasUserBet = computed(() => {
  const myBets = userStore.getMyBets()
  if (!myBets) return false
  const pendingBets = myBets.filter(bet => bet.status == 'pending')
  return pendingBets.some(bet => bet.event?.id === props.event.id)
})

const hasUserCombo = computed(() => {
  const myBets = userStore.getMyBets()
  if (!myBets) return false
  const pendingBets = myBets.filter(bet => bet.status == 'pending')
  return pendingBets.some(bet => 
    bet.bet_type === 'combo' && 
    bet.items.some(item => item.event.id === props.event.id)
  )
})

</script>


<template>
  <v-card
    class="event-card event-link"
    elevation="3"
    tag="a"
    :to="{ name: 'event', params: { id: event.id } }"
  >
    <!-- Event Header -->
    <v-card-title class="pb-2">
      <div class="w-100">
        <div class="d-flex justify-space-between align-start align-sm-center gap-2 gap-sm-0">
          <v-chip
            :color="getEventStatus(event.commence_time).color"
            :size="$vuetify.display.xs ? 'x-small' : 'small'"
            class="mb-2"
          >
            {{ getEventStatus(event.commence_time).text }}
          </v-chip>
          <v-chip
            color="primary"
            :size="$vuetify.display.xs ? 'x-small' : 'small'"
            variant="outlined"
            class="mb-2"
          >
            {{ formatSportTitle(event.sport_key) }}
          </v-chip>
        </div>
        
        <!-- Match Info -->
        <div class="match-info mt-2">
          <div class="teams-container">
            <div class="team home-team">
              <v-icon class="team-icon d-none d-sm-flex">mdi-home</v-icon>
              <span class="team-name">{{ event.home_team }}</span>
              <span v-if="event.score && event.score.completed" class="team-score">{{ event.score.score_1 }}</span>
            </div>
            
            <div class="vs-section">
              <span v-if="event.score?.completed" class="score-separator">-</span>
            </div>
            
            <div class="team away-team">
              <span v-if="event.score && event.score.completed" class="team-score">{{ event.score.score_2 }}</span>
              <span class="team-name">{{ event.away_team }}</span>
              <v-icon class="team-icon d-none d-sm-flex">mdi-airplane</v-icon>
            </div>
          </div>
        </div>
      </div>
    </v-card-title>

    <!-- Event Time -->
    <v-card-subtitle class="pb-3 text-center text-sm-start">
      <v-icon size="16" class="mr-1">mdi-clock-outline</v-icon>
      {{ $d(event.commence_time, 'long') }}
    </v-card-subtitle>

    <!-- Odds Section -->
    <v-card-text class="pt-0">
      <OddsCard :event="event" />
    </v-card-text>

    <div class="myBets-indicators">
      <v-icon v-if="hasUserBet"
        size="small"
        color="success"
        :title="$t('events.betting.you_have_bet_on_this_event')"
      >mdi-target</v-icon>
      <v-icon v-if="hasUserCombo"
        size="small"
        color="grey-darken-2"
        :title="$t('events.betting.this_event_is_in_your_combo')"
      >mdi-shape-outline</v-icon>
    </div>

  </v-card>
</template>


<style scoped>
.myBets-indicators {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  font-size: 0.9rem;
  opacity: 0.8;
}
.event-link {
  text-decoration: none;
  color: inherit;
  display: block;
}
.event-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
}

.event-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.match-info {
  width: 100%;
}

.teams-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

@media (max-width: 599px) {
  .teams-container {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
}

.team {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
}

@media (max-width: 599px) {
  .team {
    flex-direction: column;
    gap: 4px;
    min-width: 100%;
  }
}

.home-team {
  justify-content: flex-start;
}

.away-team {
  justify-content: flex-end;
}

@media (max-width: 599px) {
  .home-team,
  .away-team {
    justify-content: center;
  }
}

.team-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: #ddd;
}

@media (max-width: 599px) {
  .team-name {
    font-size: 1rem;
  }
}

@media (max-width: 400px) {
  .team-name {
    font-size: 0.9rem;
  }
}

.team-score {
  font-weight: 700;
  font-size: 1.3rem;
  color: #9cb69d;
  background: #2c2f2c;
  padding: 4px 8px;
  border-radius: 6px;
  min-width: 30px;
  text-align: center;
  display: inline-block;
}

@media (max-width: 599px) {
  .team-score {
    font-size: 1.1rem;
    padding: 3px 6px;
    min-width: 26px;
  }
}

.team-icon {
  color: #666;
}

.vs-section {
  display: flex;
  align-items: center;
  color: #999;
  min-width: 20px;
  justify-content: center;
}

@media (max-width: 599px) {
  .vs-section {
    min-width: 16px;
    margin: 4px 0;
    display: none;
  }
}

.score-separator {
  font-weight: 700;
  font-size: 1.2rem;
  color: #666;
}

@media (max-width: 599px) {
  .score-separator {
    font-size: 1rem;
  }
}


</style>